import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  Select,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { bulkSaveRates, getRateForDate } from '../../services/api';
import RateUpdateProgressPanel from './RateUpdateProgressPanel';
import RateActionSuccessDialog, { RATE_SUCCESS_COPY } from './RateActionSuccessDialog';
import {
  inventoryDrawerFooterSx,
  inventoryDrawerHeaderSx,
} from '../../utils/inventoryMobileLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';

const THEME_COLOR = '#4ce1b6';

const DAY_OPTIONS = [
  { value: 0, label: 'Sun', shortLabel: 'S', color: '#FF6B6B' },
  { value: 1, label: 'Mon', shortLabel: 'M', color: '#4ECDC4' },
  { value: 2, label: 'Tue', shortLabel: 'T', color: '#45B7D1' },
  { value: 3, label: 'Wed', shortLabel: 'W', color: '#FFA07A' },
  { value: 4, label: 'Thu', shortLabel: 'T', color: '#98D8C8' },
  { value: 5, label: 'Fri', shortLabel: 'F', color: '#F7DC6F' },
  { value: 6, label: 'Sat', shortLabel: 'S', color: '#BB8FCE' },
];

const EMPTY_RATE = {
  base_rate: '',
  single_occupancy_rate: '',
  extra_adult_rate: '',
  extra_child_rate: '',
  min_los: '',
  max_los: '',
  available: true,
  currency: 'INR',
};

const rateFromApi = (rateData = {}, { empty = false } = {}) => ({
  base_rate: rateData.base_rate ?? '',
  single_occupancy_rate: rateData.single_occupancy_rate ?? '',
  extra_adult_rate: rateData.extra_adult_rate ?? '',
  extra_child_rate: rateData.extra_child_rate ?? '',
  min_los: empty ? 1 : rateData.min_los ?? '',
  max_los: empty ? 30 : rateData.max_los ?? '',
  available: rateData.available !== false,
  currency: rateData.currency || 'INR',
});

/**
 * PMS Update drawer. Owns its form state so typing does not re-render Manage Rates.
 */
function ManageRateUpdateDrawer({
  open,
  onClose,
  roomTypes = [],
  ratePlans = [],
  isDark,
  themeColors,
  initialContext = null,
  onSaved,
}) {
  const [selectedRoomType, setSelectedRoomType] = useState('');
  const [selectedRatePlan, setSelectedRatePlan] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [daySelectionType, setDaySelectionType] = useState('custom');
  const [selectedDays, setSelectedDays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [selectedRate, setSelectedRate] = useState(EMPTY_RATE);
  const [isSaving, setIsSaving] = useState(false);
  const [updateProgress, setUpdateProgress] = useState({ phase: 'idle', total: 0, result: null });
  const [errorMessage, setErrorMessage] = useState(null);
  const [successDialog, setSuccessDialog] = useState(null);
  const drawerRootRef = useRef(null);

  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? themeColors.surface : '#ffffff'),
    [isDark, themeColors.surface]
  );

  const rooms = useMemo(() => (Array.isArray(roomTypes) ? roomTypes : []), [roomTypes]);
  const plans = useMemo(() => (Array.isArray(ratePlans) ? ratePlans : []), [ratePlans]);

  const resetForm = useCallback(async () => {
    const ctx = initialContext || {};
    const today = moment().startOf('day');
    let fromDate;
    let toDate;
    if (ctx.date) {
      fromDate = moment(ctx.date).startOf('day');
      if (fromDate.isBefore(today, 'day')) fromDate = today.clone();
      toDate = fromDate.clone().add(1, 'day');
    } else {
      fromDate = moment().add(1, 'day').startOf('day');
      toDate = fromDate.clone().add(1, 'day');
    }

    const roomId =
      ctx.roomTypeId != null && ctx.roomTypeId !== '' && String(ctx.roomTypeId) !== 'all'
        ? String(ctx.roomTypeId)
        : rooms[0]
          ? String(rooms[0].id)
          : '';
    const planId = ctx.ratePlanId != null ? String(ctx.ratePlanId) : plans[0] ? String(plans[0].id) : '';

    setDateRange([fromDate, toDate]);
    setDaySelectionType('custom');
    setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
    setSelectedRoomType(roomId);
    setSelectedRatePlan(planId);
    setSelectedRate(rateFromApi({}, { empty: ctx.empty !== false && !ctx.date }));
    setErrorMessage(null);
    setSuccessDialog(null);
    setUpdateProgress({ phase: 'idle', total: 0, result: null });

    if (!ctx.date || !roomId || !planId) return;
    try {
      const rateData = await getRateForDate(
        fromDate.format('YYYY-MM-DD'),
        roomId,
        planId,
        ctx.channelId
      );
      setSelectedRate(rateFromApi(rateData));
    } catch {
      setSelectedRate(rateFromApi({}));
    }
  }, [initialContext, rooms, plans]);

  useEffect(() => {
    if (open) resetForm();
    // Only reset when the drawer opens, not when listings refresh behind it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || rooms.length === 0) return;
    if (!rooms.some((room) => String(room.id) === String(selectedRoomType))) {
      setSelectedRoomType(String(rooms[0].id));
    }
  }, [open, rooms, selectedRoomType]);

  const updateRateField = (field) => (event) => {
    const value = event.target.value;
    setSelectedRate((prev) => (prev[field] === value ? prev : { ...prev, [field]: value }));
  };

  const collectDates = () => {
    if (!dateRange[0] || !dateRange[1]) return [];
    const days =
      daySelectionType === 'weekdays'
        ? [1, 2, 3, 4, 5]
        : daySelectionType === 'weekend'
          ? [0, 6]
          : selectedDays;
    const dates = [];
    let current = moment(dateRange[0]).startOf('day');
    const end = moment(dateRange[1]).startOf('day');
    const lastNight = end.isSame(current, 'day') ? end : end.clone().subtract(1, 'day');
    while (current.isSameOrBefore(lastNight, 'day')) {
      if (days.includes(current.day())) dates.push(current.format('YYYY-MM-DD'));
      current.add(1, 'day');
    }
    return dates;
  };

  const handleSave = async () => {
    setErrorMessage(null);
    setSuccessDialog(null);
    if (!selectedRoomType || !dateRange[0] || !dateRange[1] || selectedRate.base_rate === '') {
      setErrorMessage('Please select a date range and enter a base rate');
      return;
    }
    if (!selectedRatePlan) {
      setErrorMessage('Please select a meal plan');
      return;
    }
    if (dateRange[0].isBefore(moment().startOf('day'), 'day')) {
      setErrorMessage('Cannot set rates for past dates');
      return;
    }
    if (dateRange[1].diff(dateRange[0], 'days') > 365) {
      setErrorMessage('Date range cannot exceed 1 year');
      return;
    }
    const dates = collectDates();
    if (!dates.length) {
      setErrorMessage('No dates match the selected weekdays');
      return;
    }

    const roundToTwoDecimals = (value) => {
      if (value === null || value === undefined || value === '') return null;
      const rounded = Math.round(Number(value) * 100) / 100;
      return Number.isNaN(rounded) ? null : parseFloat(rounded.toFixed(2));
    };

    const bulkUpdates = dates.map((dateStr) => ({
      date: dateStr,
      room_type_id: selectedRoomType,
      rate_plan_id: parseInt(selectedRatePlan, 10),
      channel_id: 'pms',
      base_rate: roundToTwoDecimals(selectedRate.base_rate) || 0,
      single_occupancy_rate: roundToTwoDecimals(selectedRate.single_occupancy_rate),
      extra_adult_rate: roundToTwoDecimals(selectedRate.extra_adult_rate),
      extra_child_rate: roundToTwoDecimals(selectedRate.extra_child_rate),
      min_los: selectedRate.min_los !== '' ? parseInt(selectedRate.min_los, 10) : null,
      max_los: selectedRate.max_los !== '' ? parseInt(selectedRate.max_los, 10) : null,
      available: selectedRate.available,
      currency: selectedRate.currency,
    }));

    setIsSaving(true);
    setUpdateProgress({ phase: 'saving', total: bulkUpdates.length, result: null });
    try {
      const result = await bulkSaveRates(bulkUpdates);
      setUpdateProgress({ phase: 'done', total: bulkUpdates.length, result });
      if ((result?.failed || 0) > 0 && (result?.successful || 0) === 0) {
        setErrorMessage(
          (result?.results || []).find((row) => !row.success && !row.skipped)?.error
          || 'All rate updates failed'
        );
        return;
      }
      if (typeof onSaved === 'function') await onSaved();
      if ((result?.failed || 0) === 0) {
        setSuccessDialog(RATE_SUCCESS_COPY.updateRates);
      }
    } catch (error) {
      setUpdateProgress({ phase: 'idle', total: 0, result: null });
      setErrorMessage(
        error.response?.data?.error || error.message || 'Error saving rate. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const chipSx = (filled, color) => ({
    cursor: 'pointer',
    fontWeight: filled ? 600 : 400,
    backgroundColor: filled ? color : 'transparent',
    color: filled ? '#fff' : themeColors.subtleText,
    borderColor: filled ? color : themeColors.surfaceBorder,
    borderWidth: filled ? 2 : 1,
    borderStyle: 'solid',
    height: 36,
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => !isSaving && onClose()}
      sx={getDrawerRootSx()}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600 },
          overflow: 'hidden',
          backgroundColor: isDark ? themeColors.surface : '#ffffff',
        },
      }}
    >
      <Box
        ref={drawerRootRef}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            ...inventoryDrawerHeaderSx(isDark),
            position: 'relative',
          }}
        >
          <Typography variant="h6" sx={{ color: themeColors.text, fontWeight: 600 }}>
            Manage Rates
          </Typography>
          <IconButton
            onClick={onClose}
            disabled={isSaving}
            sx={{
              color: themeColors.subtleText,
              '&:hover': {
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {isSaving && (
            <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2 }} />
          )}
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', p: 3 }}>
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Room Type</InputLabel>
                <Select
                  value={selectedRoomType}
                  label="Room Type"
                  onChange={(event) => setSelectedRoomType(event.target.value)}
                  MenuProps={drawerOverlays.selectMenuProps}
                >
                  {rooms.map((roomType) => (
                    <MenuItem key={roomType.id} value={String(roomType.id)}>
                      {roomType.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Meal Plan</InputLabel>
                <Select
                  value={selectedRatePlan}
                  label="Meal Plan"
                  onChange={(event) => setSelectedRatePlan(event.target.value)}
                  MenuProps={drawerOverlays.selectMenuProps}
                >
                  {plans.map((plan) => (
                    <MenuItem key={plan.id} value={String(plan.id)}>
                      {plan.name} {plan.meal_plan ? `(${plan.meal_plan})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="From"
                  value={dateRange[0]}
                  minDate={moment().startOf('day')}
                  onChange={(newValue) => {
                    if (!newValue) return;
                    const newFrom = newValue.isBefore(moment().startOf('day'), 'day')
                      ? moment().startOf('day')
                      : newValue;
                    const maxTo = newFrom.clone().add(1, 'year');
                    let newTo = newFrom.clone().add(1, 'day');
                    if (newTo.isAfter(maxTo)) newTo = maxTo;
                    setDateRange([newFrom, newTo]);
                  }}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
                <DatePicker
                  label="To"
                  value={dateRange[1]}
                  onChange={(newValue) => {
                    if (newValue && dateRange[0] && newValue.isAfter(dateRange[0])) {
                      const maxTo = dateRange[0].clone().add(1, 'year');
                      setDateRange([dateRange[0], newValue.isAfter(maxTo) ? maxTo : newValue]);
                    }
                  }}
                  minDate={dateRange[0]?.clone().add(1, 'day')}
                  maxDate={dateRange[0]?.clone().add(1, 'year')}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Date range cannot exceed 1 year. Past dates are not allowed. To is checkout and is not included.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Day Selection
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', pl: 2 }}>
                {[
                  { value: 'custom', label: 'Custom', days: [0, 1, 2, 3, 4, 5, 6] },
                  { value: 'weekdays', label: 'Weekdays', days: [1, 2, 3, 4, 5] },
                  { value: 'weekend', label: 'Weekend', days: [0, 6] },
                ].map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Radio
                        checked={daySelectionType === opt.value}
                        onChange={() => {
                          setDaySelectionType(opt.value);
                          setSelectedDays(opt.days);
                        }}
                      />
                    }
                    label={opt.label}
                  />
                ))}
              </Box>
              {daySelectionType === 'custom' && (
                <Box sx={{ mt: 2, pl: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {DAY_OPTIONS.map((day) => {
                      const isSelected = selectedDays.includes(day.value);
                      return (
                        <Chip
                          key={day.value}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                {day.shortLabel}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                {day.label}
                              </Typography>
                            </Box>
                          }
                          onClick={() => {
                            setSelectedDays((prev) =>
                              prev.includes(day.value)
                                ? prev.filter((value) => value !== day.value)
                                : [...prev, day.value]
                            );
                          }}
                          variant={isSelected ? 'filled' : 'outlined'}
                          sx={chipSx(isSelected, day.color)}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Rates
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Base Rate"
                    value={selectedRate.base_rate}
                    onChange={updateRateField('base_rate')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Single Occupancy"
                    value={selectedRate.single_occupancy_rate}
                    onChange={updateRateField('single_occupancy_rate')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    size="small"
                    placeholder="Optional"
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Extra Adult"
                    value={selectedRate.extra_adult_rate}
                    onChange={updateRateField('extra_adult_rate')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    size="small"
                    placeholder="Optional"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Extra Child"
                    value={selectedRate.extra_child_rate}
                    onChange={updateRateField('extra_child_rate')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                    size="small"
                    placeholder="Optional"
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Length of Stay (LOS) Restrictions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Min LOS"
                    value={selectedRate.min_los}
                    onChange={updateRateField('min_los')}
                    variant="outlined"
                    size="small"
                    placeholder="0 (no minimum)"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max LOS"
                    value={selectedRate.max_los}
                    onChange={updateRateField('max_los')}
                    variant="outlined"
                    size="small"
                    placeholder="30 (default max)"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Updates PMS rates only. Use Group Update for Channel Manager occupancy plans and OTAs.
            </Typography>
          </LocalizationProvider>
        </Box>

        <Box sx={{ px: 2 }}>
          <RateUpdateProgressPanel
            phase={updateProgress.phase}
            total={updateProgress.total}
            result={updateProgress.result}
            pmsOnly
          />
        </Box>

        <Box
          sx={{
            p: 1.5,
            ...inventoryDrawerFooterSx(isDark),
            display: 'flex',
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            disabled={isSaving}
            sx={{
              borderColor: themeColors.surfaceBorder,
              color: themeColors.subtleText,
              '&:hover': {
                borderColor: THEME_COLOR,
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                color: THEME_COLOR,
              },
              '&:disabled': {
                borderColor: themeColors.surfaceBorder,
                color: isDark ? '#666' : '#999',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSave}
            disabled={isSaving}
            sx={{
              backgroundColor: THEME_COLOR,
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: THEME_COLOR,
                opacity: 0.9,
                boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
              },
              '&:disabled': {
                backgroundColor: themeColors.surfaceBorder,
                color: isDark ? '#999' : '#666',
              },
            }}
          >
            {isSaving ? (
              <CircularProgress size={20} sx={{ color: '#000' }} />
            ) : updateProgress.phase === 'done' ? (
              'Done'
            ) : (
              'Save'
            )}
          </Button>
        </Box>
        <RateActionSuccessDialog
          open={Boolean(successDialog)}
          title={successDialog?.title}
          message={successDialog?.message}
          container={() => drawerRootRef.current || undefined}
          onClose={() => {
            setSuccessDialog(null);
            onClose();
          }}
        />
      </Box>
    </Drawer>
  );
}

export default React.memo(ManageRateUpdateDrawer);
